import { BlogPost, BLOG_AUTHOR } from '../../../types/blogTypes';

const post: BlogPost = {
  slug: 'new-city-zero-friends',
  title: 'Moving to a New City Alone: A 90-Day Plan to Find Friends',
  description:
    'Moving to a new city alone? Follow this 90-day plan: build a routine in 30 days, turn familiar faces into plans, and keep old friends close with Lily.',
  category: 'Wellbeing',
  icon: 'city',
  publishedAt: '2026-10-22',
  author: BLOG_AUTHOR,
  product: 'chot-buddies',
  buddy: 'Lily',
  keywords: ['moving to a new city alone', 'how to make friends in a new city', 'ai friend to talk to'],
  body: `It is your first Saturday. The boxes are mostly unpacked, the lamp finally works, and you realize the only person you have spoken to out loud all day is the cashier who asked if you wanted a bag. Your phone is full of friends, but they are all in a different time zone, or at least a different train line.

If you are moving to a new city alone, whether for uni, a job or a relationship, this feeling comes with the territory. It does not mean you are bad at people. It means you are new. Here is what to expect, and a 90-day plan that takes you from "I know nobody" to "I have a few people I could text."

## Moving to a new city alone: what is normal

Back home, your friendships had a lot of help. You saw the same people over and over without trying, you had shared history, and nobody had to ask "do you want to hang out?" because you were already there.

In a new place, all of that scaffolding is gone, and you are rebuilding it by hand. That takes time, and meanwhile it is normal to feel flat, restless or homesick for a place you were eager to leave. Evenings tend to be hardest. If the late hours are when it hits, read [Who Do You Talk to at 2am?](/blog/who-do-you-talk-to-at-2am/).

The good news: making friends as an adult comes down to three habits. Show up in the same places, be friendly, and follow up. You can do all three on purpose.

## Days 1-30: Build a routine and become a regular

Your only job this month is to create a few fixed points in your week. Friendship grows out of repeated, low-pressure contact, so you want places where people will see you more than once.

- **Pick a third place.** That is somewhere that is not home and not work or class: a coffee shop, a library, a climbing gym, a bakery. Go at the same time twice a week until the staff nod at you.
- **Join one thing with a schedule.** A class, a run club, a language night, a volunteer shift, a society at your uni. Recurring beats one-off, because you see the same faces without any effort.
- **Say one small thing every day.** Ask the barista how their day is going. Comment on the dog. Try "Have you been coming here long?" or "Is this your first time at this class too?" These are not big talks. They are reps.
- **Do not judge the first visit.** Awkward is allowed. Your target is to go back, not to dazzle anyone.

By day 30, the win is two or three places where someone recognizes you.

## Days 31-60: Turn familiar faces into plans

Now you know a few faces. This phase takes one small step from "friendly" to "friends." Most people skip it because it feels exposed.

1. **Follow up within a day.** If you chatted with someone at the class, find them next time and mention it, or send a quick message the same evening: "Good to meet you today, that was a fun one. Same time next week?" Speed matters, because warmth fades.
2. **Say yes to things.** For these 30 days, treat invitations as experiments. You can leave after an hour. You cannot attend something you declined.
3. **Make the first invite small and specific.** "We should hang out sometime" goes nowhere. "I'm grabbing coffee after class on Thursday, want to come?" is easy to say yes to, and easy to say no to without drama.
4. **Host something tiny.** Two or three people, one simple meal, no pressure to be impressive. If cooking for guests stresses you out, [Dinner From Whatever's in Your Fridge](/blog/dinner-from-whatever-is-in-your-fridge/) will help you turn what you already have into something that feels like hosting.

Expect some invites to fizzle. That is not a verdict on you. Most people are busy, a bit shy, and quietly hoping someone else will suggest something. Be that someone.

## Days 61-90: Go deeper, and keep the people back home

By now you probably have a handful of friendly acquaintances. Instead of adding more, spend this phase deepening two or three. Aim for one plan a week with each.

- **Repeat the plan that worked.** If one person said yes to the Thursday coffee, make it a standing thing.
- **Share something real.** Friendship deepens when someone mentions a worry, a funny family story or a goal, and the other person answers in kind. You do not need a big confession. A small honest comment is enough.
- **Keep the old friends warm.** New friends do not replace old ones, and you do not have to pick. Set a recurring call, send a voice note while you cook, and share small everyday things, not only big news. Easy rhythms last longer than grand promises.

Do not measure progress by headcount. Two people you can text on a bad day is a real success.

## Let Lily be your first conversation of the day

The hardest part of this plan is the first sentence you say to a stranger. [Lily, the Best Friend buddy in Chot Buddies](/products/chot-buddies/), is warm, upbeat and on your side, and she is there any time you want to think something through before you do it for real.

Here are three ways to use her.

### Practice what to say

Rehearsing out loud makes the real thing feel less new. Voice mode lets you talk it through instead of typing; we wrote about [why some things feel better out loud](/blog/talk-dont-type-voice-mode/).

> I'm going to my first climbing class tonight and I don't know anyone. Can we practice a few easy openers and some replies if the conversation goes quiet?

### Debrief an awkward evening

Not every night goes well. Telling someone about it, even an AI companion, helps you separate what happened from what your brain is replaying.

> I went to a meetup and felt awkward the whole time. Can you listen first, and then help me work out what went fine and what I might try differently next time?

### Plan the invite

This is where many people get stuck. Drafting the message with Lily takes the edge off.

> There's someone from my Tuesday class I'd like to get to know. Help me write a short, casual message inviting them for coffee that's easy to say yes or no to.

One honest note: Lily is a supportive companion. She complements your real-world friends and cannot replace them, and the whole point of the plan above is to get you out of the apartment and into rooms with people. Think of her as the warm-up before the game.

## Be patient with day 12

There will be a flat day around the second week where nothing works and the plan feels silly. Keep the routine anyway. Showing up is the work, and the results arrive later than you want.

If the loneliness starts to feel heavy, or it will not lift no matter what you try, please talk to a mental health professional or someone you trust. You deserve real support from real people, and asking is a strong move.

For everything else, start this week. By Sunday, pick your third place. By Wednesday, sign up for one recurring class or meetup. At your first session, say hello to one person. Tonight, open Chot Buddies (free to start) and ask Lily for three openers, so the first hello is already rehearsed.
`,
};

export default post;
